// Iter 90g — Structured editor for Support & Escalations contact tables.
//
// Why this exists:
//   The old approach dropped the raw HTML table into a contentEditable
//   RichTextEditor. Editing a phone number required navigating tiny cells
//   and easily broke the styling. Admins asked for something forgiving.
//
// What it does:
//   1. Parses the existing HTML into a normalised {intro, rows[]} shape.
//   2. Renders a form: each row = Label · Availability · Contacts[]
//      where contacts auto-detect email / phone and become clickable.
//   3. On any edit, serialises back to the exact same HTML `<table>`
//      shape the Dashboard already renders — no visual drift.
//   4. Keeps a "Show HTML" advanced escape hatch for power users.
//
// The bulk of the code here is defensive parsing so admins never lose
// data even if the stored HTML is old, hand-edited, or plain text.

import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, GripVertical, ChevronUp, ChevronDown, Code2, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RichTextEditor } from "@/components/RichTextEditor";

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const digits = (s) => (s || "").replace(/\D/g, "");
const looksLikePhone = (s) => digits(s).length >= 7;

// --- Serialisers ----------------------------------------------------------
const escapeHtml = (s) => (s || "")
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;");

const linkifyContact = (raw) => {
  const c = (raw || "").trim();
  if (!c) return "";
  if (c.startsWith("<a ")) return c;                     // already linked
  const m = c.match(/^(.*?)(?:\s*\((.+)\))?$/);          // "text (note)"
  const primary = (m[1] || c).trim();
  const note    = (m[2] || "").trim();
  const noteHtml = note ? ` (${escapeHtml(note)})` : "";
  if (EMAIL_RX.test(primary)) {
    return `<a href="mailto:${primary}">${escapeHtml(primary)}</a>${noteHtml}`;
  }
  if (looksLikePhone(primary)) {
    const d = digits(primary);
    // Prepend +1 for 10-digit NANP numbers; keep leading + otherwise.
    const tel = primary.trim().startsWith("+") ? "+" + d
              : d.length === 10 ? "+1" + d
              : "+" + d;
    return `<a href="tel:${tel}">${escapeHtml(primary)}</a>${noteHtml}`;
  }
  return escapeHtml(c);
};

const availabilityToHtml = (raw) => {
  const s = (raw || "").trim();
  if (!s) return "";
  // If the admin used **bold** shorthand, honour it. Otherwise
  // bold any 24/7 tokens automatically so the styling matches
  // the reference design.
  const withBold = s
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/(24\/7\/365|24\/7)/g, "<strong>$1</strong>");
  return withBold.split(/\n/).map(escapeHtml)  // keep HTML from bold conversion
    // undo the escape on our injected <strong> tags
    .map((s2) => s2.replace(/&lt;(\/?)strong&gt;/g, "<$1strong>"))
    .join("<br>");
};

export const escalationRowsToHtml = ({ intro, rows }) => {
  const chunks = [];
  const introTxt = (intro || "").trim();
  if (introTxt) chunks.push(`<p>${escapeHtml(introTxt)}</p>`);
  const bodyRows = (rows || []).filter((r) => (r.label || "").trim());
  if (bodyRows.length) {
    chunks.push("<table>");
    chunks.push("  <tbody>");
    for (const r of bodyRows) {
      const label = `<strong>${escapeHtml((r.label || "").trim())}</strong>`;
      const avail = availabilityToHtml(r.availability || "");
      const contacts = (r.contacts || [])
        .map((c) => linkifyContact(c))
        .filter(Boolean)
        .join("<br>");
      chunks.push("    <tr>");
      chunks.push(`      <td>${label}</td>`);
      chunks.push(`      <td>${avail}</td>`);
      chunks.push(`      <td>${contacts}</td>`);
      chunks.push("    </tr>");
    }
    chunks.push("  </tbody>");
    chunks.push("</table>");
  }
  return chunks.join("\n");
};

// --- Deserialiser ---------------------------------------------------------
// Reverses the serializer: pulls text out of the anchors while keeping
// (note) suffixes, and turns <br>/<strong>24/7</strong> back into plain
// admin-friendly text like "**24/7/365**\n(After hours…)".
const nodeToPlainAvailability = (td) => {
  if (!td) return "";
  // Bold-tag → **wrap** for our own shorthand.
  const clone = td.cloneNode(true);
  clone.querySelectorAll("strong,b").forEach((el) => {
    el.replaceWith(`**${el.textContent}**`);
  });
  clone.querySelectorAll("br").forEach((el) => el.replaceWith("\n"));
  return (clone.textContent || "").trim();
};

const nodeToPlainContacts = (td) => {
  if (!td) return [];
  const clone = td.cloneNode(true);
  clone.querySelectorAll("br").forEach((el) => el.replaceWith("\n"));
  // Preserve the visible label of each <a> — the linkifier will
  // recreate the correct href on save.
  clone.querySelectorAll("a").forEach((el) => {
    el.replaceWith(el.textContent || "");
  });
  return (clone.textContent || "")
    .split(/\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
};

export const escalationHtmlToRows = (raw) => {
  const html = (raw || "").trim();
  if (!html) return { intro: "", rows: [] };
  // Plain-text legacy: split on blank lines, no rows yet.
  if (!/</.test(html)) {
    return { intro: html, rows: [] };
  }
  const doc = new DOMParser().parseFromString(html, "text/html");
  const introEl = doc.querySelector("p");
  const intro = introEl ? (introEl.textContent || "").trim() : "";
  const rows = [];
  doc.querySelectorAll("table tr").forEach((tr) => {
    const tds = tr.querySelectorAll("td");
    if (tds.length < 2) return;
    const [labelTd, availTd, contactsTd] = [tds[0], tds[1], tds[2]];
    // Label: strip the leading <strong>.
    const labelClone = labelTd.cloneNode(true);
    labelClone.querySelectorAll("strong,b").forEach((el) => el.replaceWith(el.textContent || ""));
    const label = (labelClone.textContent || "").trim();
    if (!label) return;
    rows.push({
      label,
      availability: nodeToPlainAvailability(availTd),
      contacts: nodeToPlainContacts(contactsTd),
    });
  });
  return { intro, rows };
};

// --- Component ------------------------------------------------------------
const BLANK_ROW = () => ({ label: "", availability: "", contacts: [""] });

export function EscalationTableEditor({
  value, onChange, disabled = false, testid = "escalation-editor",
  helper = "",
}) {
  const parsed = useMemo(() => escalationHtmlToRows(value), [value]);
  const [intro, setIntro] = useState(parsed.intro);
  const [rows,  setRows]  = useState(parsed.rows.length ? parsed.rows : [BLANK_ROW()]);
  const [mode,  setMode]  = useState("form");           // "form" | "html"

  // Rehydrate whenever the parent value swaps out from underneath
  // (e.g. Reset / template button).
  useEffect(() => {
    setIntro(parsed.intro);
    setRows(parsed.rows.length ? parsed.rows : [BLANK_ROW()]);
  }, [value]);           // eslint-disable-line react-hooks/exhaustive-deps

  // Propagate every edit as fresh HTML.
  const commit = (nextIntro, nextRows) => {
    const html = escalationRowsToHtml({ intro: nextIntro, rows: nextRows });
    onChange?.(html);
  };
  const setIntroSafe = (v) => { setIntro(v); commit(v, rows); };
  const setRowsSafe = (nextRows) => { setRows(nextRows); commit(intro, nextRows); };

  const updateRow = (idx, patch) => {
    setRowsSafe(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const updateContact = (idx, cIdx, val) => {
    const next = [...(rows[idx].contacts || [""])];
    next[cIdx] = val;
    updateRow(idx, { contacts: next });
  };
  const addContact = (idx) => {
    const next = [...(rows[idx].contacts || []), ""];
    updateRow(idx, { contacts: next });
  };
  const removeContact = (idx, cIdx) => {
    const next = (rows[idx].contacts || []).filter((_, i) => i !== cIdx);
    updateRow(idx, { contacts: next.length ? next : [""] });
  };
  const addRow = () => setRowsSafe([...rows, BLANK_ROW()]);
  const removeRow = (idx) => setRowsSafe(rows.filter((_, i) => i !== idx));
  const moveRow = (idx, dir) => {
    const t = idx + dir;
    if (t < 0 || t >= rows.length) return;
    const copy = [...rows];
    [copy[idx], copy[t]] = [copy[t], copy[idx]];
    setRowsSafe(copy);
  };

  if (mode === "html") {
    return (
      <div className="space-y-2" data-testid={`${testid}-html`}>
        <RichTextEditor value={value || ""} onChange={onChange}
                        disabled={disabled}
                        placeholder="Rich HTML — headings, tables, links…"
                        testid={`${testid}-html-input`} />
        <div className="flex items-center justify-between">
          <p className="text-[11px] text-slate-500">Advanced mode. Switch back to the form to edit contacts row-by-row.</p>
          <button type="button" onClick={() => setMode("form")}
                  data-testid={`${testid}-mode-form`}
                  className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50">
            <Eye className="h-3 w-3" /> Simple form
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid={testid}>
      {/* Intro row */}
      <div className="space-y-1">
        <Label className="text-xs text-slate-600">Intro paragraph (optional)</Label>
        <Input
          value={intro}
          disabled={disabled}
          data-testid={`${testid}-intro-input`}
          onChange={(e) => setIntroSafe(e.target.value)}
          placeholder="Reach out to the following contacts for technical support." />
      </div>

      {/* Rows list */}
      <div className="space-y-2">
        {rows.map((row, idx) => (
          <div key={idx}
               data-testid={`${testid}-row-${idx}`}
               className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
            <div className="mb-2 flex items-center gap-2">
              <GripVertical className="h-4 w-4 text-slate-300" />
              <Input value={row.label}
                     disabled={disabled}
                     data-testid={`${testid}-row-${idx}-label`}
                     onChange={(e) => updateRow(idx, { label: e.target.value })}
                     placeholder="Label (e.g. Technical Support)"
                     className="flex-1 font-semibold" />
              <div className="flex items-center gap-1">
                <button type="button" disabled={disabled || idx === 0}
                        onClick={() => moveRow(idx, -1)}
                        data-testid={`${testid}-row-${idx}-up`}
                        className="rounded p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-700 disabled:opacity-40"
                        aria-label="Move up">
                  <ChevronUp className="h-4 w-4" />
                </button>
                <button type="button" disabled={disabled || idx === rows.length - 1}
                        onClick={() => moveRow(idx, 1)}
                        data-testid={`${testid}-row-${idx}-down`}
                        className="rounded p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-700 disabled:opacity-40"
                        aria-label="Move down">
                  <ChevronDown className="h-4 w-4" />
                </button>
                <button type="button" disabled={disabled}
                        onClick={() => removeRow(idx)}
                        data-testid={`${testid}-row-${idx}-delete`}
                        className="rounded p-1 text-rose-500 hover:bg-rose-50 hover:text-rose-700 disabled:opacity-40"
                        aria-label="Delete row">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="space-y-1">
                <Label className="text-[11px] text-slate-500">Availability / hours</Label>
                <Textarea rows={3}
                          value={row.availability}
                          disabled={disabled}
                          data-testid={`${testid}-row-${idx}-avail`}
                          onChange={(e) => updateRow(idx, { availability: e.target.value })}
                          placeholder={"Monday - Friday\n6:30 am - 5:30 pm MST/MDT\n(or **24/7/365** for always-on)"}
                          className="text-sm" />
                <p className="text-[10px] text-slate-400">Wrap any text in <code>**double asterisks**</code> to bold it — <em>24/7</em> tokens auto-bold.</p>
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] text-slate-500">Contact lines</Label>
                <div className="space-y-1.5">
                  {(row.contacts || [""]).map((c, cIdx) => (
                    <div key={cIdx} className="flex items-center gap-2">
                      <Input value={c}
                             disabled={disabled}
                             data-testid={`${testid}-row-${idx}-contact-${cIdx}`}
                             onChange={(e) => updateContact(idx, cIdx, e.target.value)}
                             placeholder="e.g. support@unitedcloud.ca — or (800) 693-3891 (Keith's number)"
                             className="text-sm" />
                      <button type="button" disabled={disabled}
                              onClick={() => removeContact(idx, cIdx)}
                              data-testid={`${testid}-row-${idx}-contact-${cIdx}-delete`}
                              className="rounded p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-700 disabled:opacity-40"
                              aria-label="Remove contact">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  <button type="button" disabled={disabled}
                          onClick={() => addContact(idx)}
                          data-testid={`${testid}-row-${idx}-add-contact`}
                          className="inline-flex items-center gap-1 rounded-full border border-dashed border-slate-300 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-500 hover:text-slate-800 hover:border-slate-400 disabled:opacity-40">
                    <Plus className="h-3 w-3" /> Add contact
                  </button>
                </div>
                <p className="text-[10px] text-slate-400">Emails become <code>mailto:</code> links; phone numbers become <code>tel:</code> links automatically. Wrap notes in parentheses, e.g. <em>(403) 671-1306 (Keith's Number)</em>.</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" variant="outline"
                onClick={addRow} disabled={disabled}
                data-testid={`${testid}-add-row`}>
          <Plus className="h-4 w-4" /> Add contact row
        </Button>
        <button type="button" onClick={() => setMode("html")}
                data-testid={`${testid}-mode-html`}
                className="ml-auto inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-500 hover:text-slate-800 hover:bg-slate-50">
          <Code2 className="h-3 w-3" /> Show HTML
        </button>
      </div>
      {helper && <p className="text-[11px] text-slate-500">{helper}</p>}
    </div>
  );
}
